import apiClient from '../helpers/apiClient'

export type AuditAction = 'login' | 'created' | 'updated' | 'deleted' | 'purchased'
export type AuditSection =
  | 'auth'
  | 'members'
  | 'memberships'
  | 'experiences'
  | 'connections'
  | 'forums'
  | 'mastermind_circles'
  | 'expertise'
  | 'audit'

export interface AuditActor {
  id: number | null
  name: string
  email: string | null
  role: string | null
  avatarUrl: string | null
}

export interface AuditTarget {
  entityType: string | null
  entityId: number | null
  label: string | null
}

export interface AuditEventListItem {
  id: number
  createdAt: string
  actor: AuditActor
  section: AuditSection | string
  action: AuditAction | string
  target: AuditTarget
  summary: string | null
}

export interface AuditChangeEntry {
  before: unknown
  after: unknown
}

export interface AuditEventDetail extends AuditEventListItem {
  ip: string | null
  userAgent: string | null
  requestId: string | null
  changes: Record<string, AuditChangeEntry> | Record<string, unknown> | null
  metadata: Record<string, unknown> | null
}

export interface AuditListParams {
  page?: number
  pageSize?: number
  actor?: string           // comma-separated user ids
  userType?: string        // comma-separated role codes
  section?: string         // comma-separated
  action?: string          // comma-separated
  dateFrom?: string        // ISO
  dateTo?: string          // ISO
  q?: string               // free text
  includeArchive?: boolean
}

export interface AuditListResponse {
  count: number
  next: string | null
  previous: string | null
  results: AuditEventListItem[]
}

const toQuery = (params: AuditListParams): Record<string, string | number> => {
  const query: Record<string, any> = {
    page: params.page,
    page_size: params.pageSize,
    actor: params.actor,
    user_type: params.userType,
    section: params.section,
    action: params.action,
    date_from: params.dateFrom,
    date_to: params.dateTo,
    q: params.q,
    include_archive: params.includeArchive ? 'true' : undefined,
  }
  Object.keys(query).forEach((k) => (query[k] === undefined || query[k] === '') && delete query[k])
  return query
}

export const getAuditEvents = async (params: AuditListParams = {}): Promise<AuditListResponse> => {
  const response = await apiClient.get('/audit/events/', { params: toQuery(params) })
  return response.data
}

export const getAuditEvent = async (id: number): Promise<AuditEventDetail> => {
  const response = await apiClient.get(`/audit/events/${id}/`)
  return response.data
}

export interface AuditExportParams extends AuditListParams {
  includeChanges?: boolean
}

/**
 * Sync-only CSV export. Backend returns a text/csv payload; we hand it back as a Blob so the
 * caller can trigger the download without leaking the file into the router.
 */
export const exportAuditEvents = async (params: AuditExportParams = {}): Promise<Blob> => {
  const query: Record<string, any> = {
    ...toQuery(params),
    include_changes: params.includeChanges ? 'true' : undefined,
  }
  Object.keys(query).forEach((k) => query[k] === undefined && delete query[k])
  const response = await apiClient.get('/audit/exports/', {
    params: query,
    responseType: 'blob',
  })
  return response.data
}
